import { useMutation } from '@apollo/client';
import { useStripe } from '@stripe/react-stripe-js';
import type { StripeFpxBankElementChangeEvent } from '@stripe/stripe-js';
import { useTranslations } from '@vocab/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { PAYMENT_GATEWAY, TEST_RECORD_SUCCEEDED_STATUS } from '../../constants';
import type { Currency } from '../../entities/Currency';
import type { UseCase } from '../../entities/enums';
import {
  CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
  CREATE_STRIPE_PAYMENT_INTENT,
} from '../../graphql';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import translations from '../../locales.vocab';
import type { HirerPaymentResult, Invoice, PaymentStatus } from '../../types';
import {
  redirectCartidQuery,
  redirectFpxQuery,
  redirectOrderidQuery,
} from '../../utils/constants';
import getReturnUrl from '../../utils/getReturnUrl';
import { handleOrderErrorPayload } from '../../utils/handleOrderError';
import {
  PaymentGeneralError,
  PaymentActionRequiredError,
  PaymentConfirmationError,
  PaymentDefaultError,
} from '../Errors/customErrors';
import { hideFpxAtom } from '../Maintenance/MaintenanceStore';

import { fpxInternetBankErrorAtom, internetBankAtom } from './FpxStore';

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  invoice: Invoice[];
  currency: Currency;
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  referenceId: string;
  cartId: string;
}

export interface UseFpxInput<T> {
  useCase: UseCase;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

// Hook to be used for general FPX payment method not specific to any consumers
const useFpx = ({
  useCase,
  invoiceInputs = {
    customerId: '',
    accountId: '',
    prn: '',
    invoice: [],
    currency: 'MYR',
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    cartId: '',
    referenceId: '',
  },
}: UseFpxInput<UseCase>) => {
  // need to cast type to both PaymentLinkInput & nonPaymentLinkInput because of optional parameters
  const { customerId, accountId, prn, invoice, email, totalForPaymentIntent } =
    invoiceInputs;

  const { cartId, referenceId } = cajaInputs;

  const [createStripePaymentIntent] = useMutation(CREATE_STRIPE_PAYMENT_INTENT);
  const [createStripePaymentIntentForInvoice] = useMutation(
    CREATE_STRIPE_INVOICE_PAYMENT_INTENT,
  );
  const stripe = useStripe();
  const { t } = useTranslations(translations);
  const { idempotencyKey, generateNewIdempotencyKey } =
    useGenerateIdempotencyKey();
  const [internetBank, setInternetBank] = useAtom(internetBankAtom);
  const setFpxInternetBankError = useSetAtom(fpxInternetBankErrorAtom);
  const isMaintenance = useAtomValue(hideFpxAtom);

  const handleOnChangeInternetBank = (e: StripeFpxBankElementChangeEvent) => {
    generateNewIdempotencyKey();
    if (e.complete && e.value) {
      setFpxInternetBankError('');
      setInternetBank(e.value);
    }
    if (e.error) {
      setFpxInternetBankError(e.error.message);
      setInternetBank('');
    }
  };

  const handleSubmit = async (): Promise<HirerPaymentResult> => {
    if (!stripe) {
      throw new PaymentDefaultError('Stripe is not loaded');
    }
    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    if (internetBank === '') {
      setFpxInternetBankError(t('idVirtualAccount.select.a.bank.error'));
      throw new PaymentActionRequiredError('User did not select a bank', 'Fpx');
    }

    const intentResponse = await createStripePaymentIntent({
      variables: {
        input: {
          orderId: referenceId,
          paymentMethod: 'fpx',
          idempotencyKey,
        },
      },
    });

    const stripePaymentIntentPayload =
      intentResponse.data?.createStripePaymentIntent;

    // fail early when typename is not StripePaymentIntentPayload
    if (
      stripePaymentIntentPayload?.__typename !== 'StripePaymentIntentPayload'
    ) {
      handleOrderErrorPayload(
        stripePaymentIntentPayload.__typename,
        stripePaymentIntentPayload.message,
      );
      throw new PaymentGeneralError(stripePaymentIntentPayload.message);
    }

    const clientSecret = stripePaymentIntentPayload.clientSecret;

    // Scalability: skip Stripe call
    if (clientSecret === 'test-record-clientsecret') {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
        paymentMethod: 'Fpx',
      };
    }

    localStorage.setItem('processingPaymentDataFPX', clientSecret);

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY.stripe,
      [
        redirectFpxQuery,
        redirectCartidQuery(cartId),
        redirectOrderidQuery(referenceId),
      ],
      useCase,
    );

    const confirmationResponse = await stripe.confirmFpxPayment(clientSecret, {
      payment_method: {
        fpx: { bank: internetBank },
      },
      return_url: frontendReturnUrl,
    });

    if (confirmationResponse.error) {
      throw new PaymentConfirmationError(
        confirmationResponse.error.message ?? 'Stripe error',
        confirmationResponse.error.type,
        confirmationResponse.error.code,
        confirmationResponse.error.decline_code,
      );
    }

    return {
      paymentIntentId: confirmationResponse.paymentIntent?.id,
      status: confirmationResponse.paymentIntent?.status,
      paymentMethod: 'Fpx',
    };
  };

  const handleSubmitForInvoice = async (): Promise<HirerPaymentResult> => {
    if (!stripe) {
      throw new PaymentDefaultError('Stripe is not loaded');
    }
    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }
    if (internetBank === '') {
      setFpxInternetBankError(t('idVirtualAccount.select.a.bank.error'));
      throw new PaymentActionRequiredError('User did not select a bank', 'Fpx');
    }

    // temporarily update id to reference, will change it after BE update
    const invoices = invoice!.map(({ id, ...rest }) => ({
      reference: id,
      ...rest,
    }));

    const intentResponse = await createStripePaymentIntentForInvoice({
      variables: {
        seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
        seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
        paymentMethod: 'fpx',
        totalForPaymentIntent,
        invoices,
        email,
        prn,
        idempotencyKey,
      },
    });

    const stripePaymentIntentPayload =
      intentResponse.data?.createStripePaymentIntentForInvoice;

    // fail early when typename is not StripePaymentIntentPayload
    if (
      stripePaymentIntentPayload?.__typename !== 'StripePaymentIntentPayload'
    ) {
      throw new PaymentGeneralError(stripePaymentIntentPayload.message);
    }

    const clientSecret = stripePaymentIntentPayload.clientSecret;

    // Scalability: skip payment gateway call
    if (clientSecret === 'test-record-clientsecret') {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
        paymentMethod: 'Fpx',
      };
    }

    localStorage.setItem('processingPaymentDataFPX', clientSecret);

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY.stripe,
      [redirectFpxQuery],
      useCase,
    );

    const confirmationResponse = await stripe.confirmFpxPayment(clientSecret, {
      payment_method: {
        fpx: { bank: internetBank },
      },
      return_url: frontendReturnUrl,
    });

    if (confirmationResponse.error) {
      throw new PaymentConfirmationError(
        confirmationResponse.error.message ?? 'Stripe error',
        confirmationResponse.error.type,
        confirmationResponse.error.code,
        confirmationResponse.error.decline_code,
      );
    }

    return {
      paymentIntentId: confirmationResponse.paymentIntent?.id,
      status: confirmationResponse.paymentIntent?.status,
      paymentMethod: 'Fpx',
    };
  };

  return {
    handleOnChangeInternetBank,
    handleSubmit,
    handleSubmitForInvoice,
    internetBank, // for testing
  };
};

export default useFpx;
